// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Which machine guides exist (S3-10), for the three things that have to agree about it: the arcade
// that links to them, the canonical link, and the `noindex` on an address that names no machine.
//
// THE DEFECT THIS STOPS
// ---------------------
// `/machines/:machineId` matches any id, and Render answers every unknown address with a 200 and
// the shell. Without this, `/machines/typo` would render "nothing here" while claiming a canonical
// of itself — an indexable page that says nothing, which a search console reports as a soft 404.
// The route table cannot know the roster, so the answer has to come from the list the build wrote.

import { MACHINE_GUIDES } from 'virtual:machine-guides'
import { guidePath, type ShellGuide } from '../seo/machineAbout'
import { machineCopy } from '../seo/pageCopy'
import type { PageMeta } from './pageMeta'

export { MACHINE_GUIDES }

/**
 * What a first-party machine's guide or machine page will call itself, known before it mounts.
 *
 * **The flicker this removes.** A cold load of `/machines/neon-nova` arrives with the right
 * prerendered `<title>`; `AppShell` then wrote "Machine guide — HCC Pinball Arcade" over it, and
 * the lazy guide put "NEON NOVA rules and modes" back a moment later. `/play/{id}` did the same
 * with "Play a machine" while the definition loaded. This answers with the values those pages
 * set — the guide's own copy, and `machineCopy` over the same title and subtitle `PlayRoute`
 * reads — so the title is written once and never changes under the reader.
 *
 * Nothing for a machine this build does not ship: a player-built machine is named only by its
 * definition, which only the page has.
 */
export function knownPageMeta(pathname: string): PageMeta | undefined {
  const match = /^\/(machines|play)\/([^/]+)\/?$/u.exec(pathname)
  if (match === null) return undefined
  const guide: ShellGuide | undefined = MACHINE_GUIDES.find((one) => one.id === match[2])
  if (guide === undefined) return undefined
  if (match[1] === 'machines') return { title: guide.pageTitle, description: guide.description, h1: guide.title }
  const copy = machineCopy(guide.subtitle === undefined ? { title: guide.title } : { title: guide.title, subtitle: guide.subtitle })
  return { title: copy.title, description: copy.description, h1: copy.h1 }
}

/** Is this address a guide's pattern with an id that has no guide? */
export function isUnknownGuide(pathname: string): boolean {
  const match = /^\/machines\/([^/]+)\/?$/u.exec(pathname)
  if (match === null) return false
  return !MACHINE_GUIDES.some((guide) => guidePath(guide.id) === `/machines/${match[1]}`)
}
